import React, { useState } from "react"
import { Row, Col, Card, Upload, Modal, Button, Typography, Spin } from "antd"
import { CloudUploadOutlined } from "@ant-design/icons"
import { useLocation, useNavigate } from "react-router-dom"
import Papa from "papaparse"
import { ExcelRenderer } from "react-excel-renderer"
import Navbar from "../../components/Navbar/Navbar"
import { capitalizeEachWord } from "../../utils/Common"
import { uploadTimesheet } from "../../utils/api/Auth"
import moment from "moment"
import { toast } from "react-hot-toast"

const { Dragger } = Upload
const { Title } = Typography

export default function UploadTimesheet() {
  const navigate = useNavigate()
  const location = useLocation()
  const formPurpose = location.state
  const [fileList, setFileList] = useState([])
  const [fileSizeExceeded, setFileSizeExceeded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  function postCallAction() {
    setIsLoading(false)
    navigate("/timesheet")
  }

  const handleRemove = (selectedFile) => {
    const newlist = fileList.filter((file) => {
      return selectedFile.uid !== file.uid
    })
    setFileList(newlist)
  }

  const handleBeforeUpload = (file) => {
    const isSizeValid = file.size / 1024 / 1024 < 10

    if (!isSizeValid) {
      setFileSizeExceeded(true)
      return Upload.LIST_IGNORE
    } else {
      setFileList([file])
    }
    return false
  }

  function chunkArray(array, chunkSize) {
    const chunks = []
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize))
    }
    return chunks
  }

  async function sendTimesheet(arrayData) {
    try {
      const response = await uploadTimesheet({ data: arrayData })
      if (response.data) {
        response.data.response?.forEach((res) => {
          if (!res?.toLowerCase().includes("successfully")) {
            toast.error(res, { duration: 2000 })
          }
        })
      }
      return response // Return the response to fulfill the promise
    } catch (error) {
      toast.error(`Error: ${error.data || error.message}`, { duration: 2000 })
      throw error // Re-throw the error to reject the promise
    }
  }

  function handleUploadTimesheet() {
    setIsLoading(true)
    const file = fileList[0]
    const extension = file.name.split(".")[1]
    if (file && extension === "csv") {
      Papa.parse(file, {
        complete: (result) => {
          const newArray = []
          result.data?.forEach((row) => {
            if (row[0] !== "" && row[1] !== "" && row[2] !== "") {
              let info = {
                dtr_id: row[0],
                work_date: row[1],
                project_id: row[2],
                is_holiday: row[3],
              }
              newArray.push(info)
            }
          })

          processTimesheets(newArray)
        },
        header: false, // If the first row of the CSV contains headers
        skipEmptyLines: true,
      })
    } else if (file && (extension === "xlsx" || extension === "xls")) {
      ExcelRenderer(file, (error, response) => {
        if (error) {
          toast.error()
        } else {
          response.rows?.forEach((row) => {
            let days = Math.floor(row[1])
            const milliseconds = Math.round(
              (row[1] - days) * 24 * 60 * 60 * 1000
            )
            let date = moment("1899-12-30")
              .add(days, "days")
              .add(milliseconds, "milliseconds")
            let formatDate = date.format("M/D/YYYY HH:mm")

            row[0] = row[0] ?? ""
            row[1] = formatDate
            row[2] = row[2] ?? ""
            row[3] = row[3] ?? ""
            row[4] = row[4] ?? ""
            row[4] = row[4] ?? ""
          })
          const arrayOfObj = response.rows.map((row) => {
            return {
              dtr_id: row[0],
              work_date: row[1],
              project_id: row[2],
              is_holiday: row[3],
            }
          })
          processTimesheets(arrayOfObj)
        }
      })
    }
  }

  const processTimesheets = async (arrayOfObj) => {
    const chunkedArray = chunkArray(arrayOfObj, 150)
    for (const chunk in chunkedArray) {
      await sendTimesheet(chunkedArray[chunk])
    }

    setTimeout(() => {
      postCallAction()
    }, 1000)
  }

  return (
    <Navbar activeNav="timesheet" formPageTitle="Upload Timesheet" isForm>
      <Card>
        <Title level={3}>{capitalizeEachWord("upload " + formPurpose)}</Title>
        <Row className="mt-3">
          <Col xs={24}>
            <Dragger
              {...{
                fileList: fileList,
                onRemove: handleRemove,
                beforeUpload: handleBeforeUpload,
                // multiple: false,
                listType: "picture",
                progress: { showInfo: true },
                data: (file) => {},
                accept: ".xlsx, .csv",
                maxCount: 1,
              }}
            >
              <CloudUploadOutlined className="upload-icon" />
              <p className="ant-upload-text">Browse Files</p>
            </Dragger>
          </Col>
        </Row>

        <Row className="d-flex justify-content-end mt-4">
          <Row>
            <Button
              danger
              className="me-2"
              onClick={() => navigate("/timesheet")}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleUploadTimesheet}
              disabled={fileList.length === 0}
              loading={isLoading}
            >
              Upload
            </Button>
          </Row>
        </Row>
      </Card>

      <Modal
        open={fileSizeExceeded}
        title={""}
        footer={null}
        onCancel={() => setFileSizeExceeded(false)}
      >
        File size must not exceed 5MB.
      </Modal>

      <Modal
        maskClosable={false}
        open={isLoading}
        footer={null}
        closeIcon={false}
      >
        <Row className="d-flex justify-content-center">
          <Title level={5}>Uploading Timesheet</Title>
        </Row>
        <Row className="d-flex justify-content-center mt-3">
          <Spin size="large" />
        </Row>
        <Row className="d-flex justify-content-center mt-3">
          Please do not close this window.
        </Row>
      </Modal>
    </Navbar>
  )
}
